import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../UI/Button';
import { useAppSelector } from '../../hooks/redux';
import { addToast } from '../../store/slices/uiSlice';
import { useAppDispatch } from '../../hooks/redux';
import { faceRecognitionService } from '../../services/faceRecognition';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

interface BiometricCaptureProps {
  onCaptureComplete?: () => void;
  disabled?: boolean;
}

const BiometricCapture: React.FC<BiometricCaptureProps> = ({ onCaptureComplete, disabled = false }) => {
  const dispatch = useAppDispatch();
  const { accessToken } = useAppSelector((state) => state.auth);
  const [capturing, setCapturing] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasBiometrics, setHasBiometrics] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [faceDetection, setFaceDetection] = useState<{
    detected: boolean;
    score: number;
    width: number;
    height: number;
    x: number;
    y: number;
    faceRatio: number;
    warning?: string;
    guide?: string;
  } | null>(null);
  const [detectionProgress, setDetectionProgress] = useState(0);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const detectionIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load face recognition models on component mount
  useEffect(() => {
    const loadModels = async () => {
      setModelsLoading(true);
      try {
        await faceRecognitionService.loadModels();
        setModelsLoaded(true);
        console.log('Face recognition models loaded');
      } catch (error: any) {
        console.error('Failed to load face recognition models:', error);
        setError('Failed to load face recognition models. Please refresh the page.');
        dispatch(addToast({ 
          message: 'Failed to load face recognition models. Please refresh the page.', 
          type: 'error' 
        }));
      } finally {
        setModelsLoading(false);
      }
    };

    loadModels();
  }, [dispatch]);

  // Real-time face detection while video is streaming
  useEffect(() => {
    if (!videoReady || !stream || !modelsLoaded || !videoRef.current) {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
      setFaceDetection(null);
      return;
    }

    const video = videoRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    
    // Start periodic face detection
    const detectFace = async () => {
      try {
        if (video.videoWidth === 0 || video.videoHeight === 0) {
          return;
        }

        const result = await faceRecognitionService.detectFacesFast(video);
        const imageArea = result.imageWidth * result.imageHeight;
        
        if (result.faces.length === 0) {
          setFaceDetection({
            detected: false,
            score: 0,
            width: 0,
            height: 0,
            x: 0,
            y: 0,
            faceRatio: 0,
            warning: 'No face detected',
            guide: 'Please position your face in the center of the frame',
          });
          drawOverlay(null, overlayCanvas, video);
          return;
        }

        if (result.faces.length > 1) {
          setFaceDetection({
            detected: false,
            score: 0,
            width: 0,
            height: 0,
            x: 0,
            y: 0,
            faceRatio: 0,
            warning: 'Multiple faces detected',
            guide: 'Please ensure only you are in the frame',
          });
          drawOverlay(null, overlayCanvas, video);
          return;
        }

        const face = result.faces[0];
        const faceArea = face.detection.width * face.detection.height;
        const faceRatio = faceArea / imageArea;
        
        let warning: string | undefined;
        let guide: string | undefined;

        if (face.detection.score < 0.5) {
          warning = 'Low detection confidence';
          guide = 'Improve lighting and face the camera directly';
        } else if (faceRatio < 0.1) {
          warning = 'Face too small';
          guide = 'Move closer to the camera';
        } else if (faceRatio > 0.4) {
          warning = 'Face too large';
          guide = 'Move further from the camera';
        } else {
          // Check if face is centered
          const centerX = result.imageWidth / 2;
          const centerY = result.imageHeight / 2;
          const faceCenterX = face.detection.x + face.detection.width / 2;
          const faceCenterY = face.detection.y + face.detection.height / 2;
          const offsetX = Math.abs(faceCenterX - centerX) / result.imageWidth;
          const offsetY = Math.abs(faceCenterY - centerY) / result.imageHeight;

          if (offsetX > 0.2 || offsetY > 0.2) {
            guide = 'Center your face in the frame';
          }
        }

        setFaceDetection({
          detected: face.detection.score >= 0.5 && faceRatio >= 0.1 && faceRatio <= 0.4,
          score: face.detection.score,
          width: face.detection.width,
          height: face.detection.height,
          x: face.detection.x,
          y: face.detection.y,
          faceRatio,
          warning,
          guide,
        });

        drawOverlay(face.detection, overlayCanvas, video);
      } catch (error) {
        console.error('Face detection error:', error);
        setFaceDetection(null);
        drawOverlay(null, overlayCanvas, video);
      }
    };

    // Detect every 500ms
    detectionIntervalRef.current = setInterval(detectFace, 500);
    detectFace(); // Initial detection

    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
    };
  }, [videoReady, stream, modelsLoaded]);

  // Helper function to draw overlay on canvas
  const drawOverlay = (
    detection: { x: number; y: number; width: number; height: number } | null,
    canvas: HTMLCanvasElement | null,
    video: HTMLVideoElement
  ) => {
    if (!canvas || !video) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!detection) {
      // Draw guide frame
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const guideSize = Math.min(canvas.width, canvas.height) * 0.4;
      
      ctx.strokeStyle = 'rgba(255, 193, 7, 0.5)';
      ctx.lineWidth = 2;
      ctx.setLineDash([10, 5]);
      ctx.strokeRect(
        centerX - guideSize / 2,
        centerY - guideSize / 2,
        guideSize,
        guideSize
      );
      return;
    }

    // Draw face detection box
    const isGood = faceDetection?.detected && !faceDetection.warning;
    ctx.strokeStyle = isGood ? 'rgba(40, 167, 69, 0.8)' : 'rgba(220, 53, 69, 0.8)';
    ctx.lineWidth = 3;
    ctx.setLineDash([]);
    ctx.strokeRect(detection.x, detection.y, detection.width, detection.height);

    // Draw center guide
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const faceCenterX = detection.x + detection.width / 2;
    const faceCenterY = detection.y + detection.height / 2;

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - 20);
    ctx.lineTo(centerX, centerY + 20);
    ctx.moveTo(centerX - 20, centerY);
    ctx.lineTo(centerX + 20, centerY);
    ctx.stroke();

    // Draw line from face center to screen center if not centered
    const offsetX = faceCenterX - centerX;
    const offsetY = faceCenterY - centerY;
    if (Math.abs(offsetX) > 20 || Math.abs(offsetY) > 20) {
      ctx.strokeStyle = 'rgba(255, 193, 7, 0.5)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(faceCenterX, faceCenterY);
      ctx.lineTo(centerX, centerY);
      ctx.stroke();
    }
  };

  // Monitor video ready state
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) {
      setVideoReady(false);
      return;
    }

    console.log('Video effect triggered:', { 
      hasVideo: !!video, 
      hasStream: !!stream, 
      srcObject: !!video.srcObject,
      readyState: video.readyState,
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight
    });

    const checkVideoReady = () => {
      const isReady = video.readyState >= video.HAVE_METADATA && 
                      video.videoWidth > 0 && 
                      video.videoHeight > 0 &&
                      video.srcObject === stream;
      
      if (isReady && !videoReady) {
        console.log('Video is ready!', {
          readyState: video.readyState,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight
        });
        setVideoReady(true);
        setError(null);
      }
    };

    // Check immediately in case video is already ready
    checkVideoReady();

    // Set up event listeners
    const handleLoadedMetadata = () => {
      console.log('Video metadata loaded');
      checkVideoReady();
      // Try to play the video
      video.play().then(() => {
        console.log('Video play() succeeded');
      }).catch((error) => {
        console.error('Error playing video:', error);
        setError('Unable to start video stream: ' + error.message);
        dispatch(addToast({ message: 'Unable to start video stream.', type: 'error' }));
      });
    };

    const handleCanPlay = () => {
      console.log('Video can play');
      checkVideoReady();
    };

    const handlePlaying = () => {
      console.log('Video is playing');
      setVideoReady(true);
      setError(null);
    };

    const handleError = (e: Event) => {
      console.error('Video error event:', e);
      setError('Video stream error occurred');
      dispatch(addToast({ message: 'Video stream error. Please try again.', type: 'error' }));
    };

    const handleStalled = () => {
      console.warn('Video stalled');
      setError('Video stream stalled');
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('error', handleError);
    video.addEventListener('stalled', handleStalled);

    // Periodic check as fallback
    const interval = setInterval(() => {
      checkVideoReady();
      // Log state for debugging
      if (!videoReady) {
        console.log('Video not ready yet:', {
          readyState: video.readyState,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          srcObject: !!video.srcObject,
          paused: video.paused
        });
      }
    }, 100);

    // Timeout to detect if video never becomes ready
    const timeout = setTimeout(() => {
      if (!videoReady && video.srcObject) {
        console.error('Video initialization timeout');
        setError('Camera initialization timed out. Please try again.');
        dispatch(addToast({ 
          message: 'Camera initialization timed out. Please check your camera and try again.', 
          type: 'error' 
        }));
      }
    }, 10000); // 10 second timeout

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('error', handleError);
      video.removeEventListener('stalled', handleStalled);
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [stream, videoReady, dispatch]);

  const startCapture = async () => {
    setError(null);
    setVideoReady(false);
    
    // Check if getUserMedia is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const errorMsg = 'Camera access is not available in this browser. Please use a modern browser with camera support.';
      console.error(errorMsg);
      setError(errorMsg);
      dispatch(addToast({ message: errorMsg, type: 'error' }));
      return;
    }

    // Check if we're on HTTPS or localhost (required for camera access)
    const isSecure = window.location.protocol === 'https:' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';
    
    if (!isSecure) {
      const errorMsg = 'Camera access requires HTTPS or localhost. Please access the site via HTTPS.';
      console.error(errorMsg);
      setError(errorMsg);
      dispatch(addToast({ message: errorMsg, type: 'error' }));
      return;
    }

    try {
      console.log('Requesting camera access...');
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
      });
      
      console.log('Camera access granted, stream obtained:', {
        active: mediaStream.active,
        videoTracks: mediaStream.getVideoTracks().length,
        audioTracks: mediaStream.getAudioTracks().length
      });

      // Check if stream has active video tracks
      const videoTracks = mediaStream.getVideoTracks();
      if (videoTracks.length === 0) {
        throw new Error('No video tracks found in stream');
      }

      const videoTrack = videoTracks[0];
      console.log('Video track info:', {
        label: videoTrack.label,
        enabled: videoTrack.enabled,
        readyState: videoTrack.readyState,
        settings: videoTrack.getSettings()
      });

      setStream(mediaStream);
      setVideoReady(false);
      
      // Wait for next tick to ensure video element is ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (videoRef.current) {
        console.log('Setting video srcObject');
        videoRef.current.srcObject = mediaStream;
        
        // Ensure video element is visible and ready
        if (videoRef.current.paused) {
          console.log('Video is paused, attempting to play...');
          videoRef.current.play().then(() => {
            console.log('Video play() succeeded immediately');
          }).catch((error) => {
            console.error('Error playing video immediately:', error);
            // Don't show error yet, let useEffect handle it
          });
        }
      } else {
        console.error('Video ref is null!');
        setError('Video element not found');
        mediaStream.getTracks().forEach(track => track.stop());
      }
    } catch (error: any) {
      console.error('Error accessing camera:', error);
      setError(error.message || 'Unknown error');
      
      let errorMessage = 'Unable to access camera. ';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow camera access in your browser settings.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera found. Please connect a camera and try again.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage += 'Camera is being used by another application.';
      } else if (error.name === 'OverconstrainedError') {
        errorMessage += 'Camera constraints could not be satisfied. Trying with default settings...';
        // Could retry with simpler constraints here
      } else {
        errorMessage += error.message || 'Please check your camera permissions and try again.';
      }
      dispatch(addToast({ message: errorMessage, type: 'error' }));
    }
  };

  const stopCapture = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (overlayCanvasRef.current) {
      const ctx = overlayCanvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);
      }
    }
    setVideoReady(false);
    setFaceDetection(null);
    setDetectionProgress(0);
  };

  const captureBiometric = async () => {
    if (!videoRef.current || !canvasRef.current) {
      dispatch(addToast({ message: 'Camera not ready. Please start the camera first.', type: 'error' }));
      return;
    }

    // Check if face is detected before capturing
    if (!faceDetection?.detected) {
      dispatch(addToast({ 
        message: faceDetection?.warning || 'Please position your face correctly before capturing.', 
        type: 'error' 
      }));
      return;
    }

    setCapturing(true);
    setDetectionProgress(0);
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Could not get canvas context');
      }

      // Check if video is ready and has valid dimensions
      if (!video.videoWidth || !video.videoHeight) {
        throw new Error('Video is not ready. Please wait for the camera to initialize.');
      }

      // Check if video stream is active
      if (video.readyState !== video.HAVE_ENOUGH_DATA) {
        throw new Error('Video stream is not ready. Please wait a moment and try again.');
      }

      // Set canvas size to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      context.drawImage(video, 0, 0);

      // Check if face recognition models are loaded
      if (!modelsLoaded) {
        throw new Error('Face recognition models are not loaded yet. Please wait and try again.');
      }

      // Perform face detection and recognition
      console.log('Detecting face in captured image...');
      setDetectionProgress(30);
      
      const faceResult = await faceRecognitionService.detectFaces(video);
      setDetectionProgress(60);
      
      if (faceResult.faces.length === 0) {
        throw new Error('No face detected. Please ensure your face is clearly visible in the camera.');
      }

      if (faceResult.faces.length > 1) {
        throw new Error('Multiple faces detected. Please ensure only one person is in the frame.');
      }

      const faceDescriptor = faceResult.faces[0];
      
      // Validate face detection quality
      if (faceDescriptor.detection.score < 0.5) {
        throw new Error('Face detection confidence is too low. Please ensure good lighting and face the camera directly.');
      }

      // Check if face is large enough (at least 10% of image)
      const faceArea = faceDescriptor.detection.width * faceDescriptor.detection.height;
      const imageArea = faceResult.imageWidth * faceResult.imageHeight;
      const faceRatio = faceArea / imageArea;
      
      if (faceRatio < 0.1) {
        throw new Error('Face is too small. Please move closer to the camera.');
      }

      console.log('Face detected successfully:', {
        score: faceDescriptor.detection.score,
        size: `${faceDescriptor.detection.width}x${faceDescriptor.detection.height}`,
        position: `(${faceDescriptor.detection.x}, ${faceDescriptor.detection.y})`,
        faceRatio: (faceRatio * 100).toFixed(1) + '%'
      });

      // Convert descriptor to base64 for storage
      setDetectionProgress(80);
      const faceTemplate = faceRecognitionService.descriptorToBase64(faceDescriptor.descriptor);

      // Save biometric data to backend
      setDetectionProgress(90);
      await axios.post(
        `${API_BASE_URL}/kyc/biometrics`,
        {
          faceTemplate,
          detectionData: {
            score: faceDescriptor.detection.score,
            width: faceDescriptor.detection.width,
            height: faceDescriptor.detection.height,
            x: faceDescriptor.detection.x,
            y: faceDescriptor.detection.y,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setDetectionProgress(100);
      setHasBiometrics(true);
      stopCapture();
      dispatch(addToast({ message: 'Biometric data captured and verified successfully', type: 'success' }));
      if (onCaptureComplete) {
        onCaptureComplete();
      }
    } catch (error: any) {
      console.error('Error capturing biometric:', error);
      const errorMessage = error.message || 'Failed to capture biometric data. Please ensure your camera is working and try again.';
      dispatch(addToast({ 
        message: errorMessage, 
        type: 'error' 
      }));
      setError(errorMessage);
      setCapturing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-[var(--color-default-font)] mb-2">
          Biometric Information
        </h3>
        <p className="text-sm text-[var(--color-subtext-color)] mb-4">
          Biometric data is securely stored and encrypted. This information is used for identity verification only.
        </p>
      </div>

      {hasBiometrics ? (
        <div className="p-4 bg-[var(--color-success-50)] border border-[var(--color-success-200)] rounded-lg">
          <p className="text-sm text-[var(--color-success-700)]">
            ✓ Biometric data has been captured and stored securely.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {!stream ? (
            <div className="space-y-4">
              {modelsLoading && (
                <div className="p-3 bg-[var(--color-warning-50)] border border-[var(--color-warning-200)] rounded-lg">
                  <p className="text-sm text-[var(--color-warning-700)]">
                    Loading face recognition models... This may take a moment.
                  </p>
                </div>
              )}
              {!modelsLoaded && !modelsLoading && (
                <div className="p-3 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-lg">
                  <p className="text-sm text-[var(--color-error-700)]">
                    Face recognition models failed to load. Please refresh the page.
                  </p>
                </div>
              )}
              <Button
                variant="outline"
                onClick={startCapture}
                disabled={disabled || modelsLoading || !modelsLoaded}
              >
                {modelsLoading ? 'Loading Models...' : 'Start Camera Capture'}
              </Button>
              
              {/* Diagnostic info */}
              {process.env.NODE_ENV === 'development' && (
                <div className="p-3 bg-[var(--color-neutral-50)] border border-[var(--color-neutral-border)] rounded text-xs">
                  <p className="font-semibold mb-1">Camera Diagnostics:</p>
                  <ul className="space-y-1 text-[var(--color-subtext-color)]">
                    <li>MediaDevices API: {navigator.mediaDevices ? '✓ Available' : '✗ Not available'}</li>
                    <li>getUserMedia: {typeof navigator.mediaDevices?.getUserMedia === 'function' ? '✓ Available' : '✗ Not available'}</li>
                    <li>Protocol: {window.location.protocol}</li>
                    <li>Hostname: {window.location.hostname}</li>
                    <li>Secure Context: {window.isSecureContext ? '✓ Yes' : '✗ No'}</li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative flex justify-center items-center">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full max-w-md rounded-lg border border-[var(--color-neutral-border)] bg-[var(--color-neutral-100)]"
                />
                <canvas
                  ref={overlayCanvasRef}
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  style={{ imageRendering: 'pixelated' }}
                />
                <canvas ref={canvasRef} className="hidden" />
                {!videoReady && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[var(--color-neutral-100)] bg-opacity-90 rounded-lg z-10">
                    <div className="text-center">
                      <p className="text-sm font-medium text-[var(--color-subtext-color)] mb-2">
                        Initializing camera...
                      </p>
                      {error && (
                        <p className="text-xs text-[var(--color-error-600)] mt-1">{error}</p>
                      )}
                    </div>
                  </div>
                )}
                {capturing && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[var(--color-neutral-900)] bg-opacity-50 rounded-lg z-10">
                    <div className="text-center space-y-3">
                      <div className="w-64 bg-[var(--color-neutral-200)] rounded-full h-2">
                        <div
                          className="bg-[var(--color-brand-primary)] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${detectionProgress}%` }}
                        />
                      </div>
                      <p className="text-sm font-medium text-white">
                        {detectionProgress < 30 && 'Capturing image...'}
                        {detectionProgress >= 30 && detectionProgress < 60 && 'Detecting face...'}
                        {detectionProgress >= 60 && detectionProgress < 80 && 'Extracting features...'}
                        {detectionProgress >= 80 && detectionProgress < 100 && 'Saving data...'}
                        {detectionProgress === 100 && 'Complete!'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Face detection status and guides */}
              {videoReady && !capturing && faceDetection && (
                <div className={`p-3 rounded-lg border ${
                  faceDetection.detected && !faceDetection.warning
                    ? 'bg-[var(--color-success-50)] border-[var(--color-success-200)]'
                    : 'bg-[var(--color-warning-50)] border-[var(--color-warning-200)]'
                }`}>
                  {faceDetection.detected && !faceDetection.warning ? (
                    <div className="flex items-center gap-2">
                      <span className="text-lg">✓</span>
                      <div>
                        <p className="text-sm font-medium text-[var(--color-success-700)]">
                          Face detected and ready
                        </p>
                        <p className="text-xs text-[var(--color-success-600)]">
                          Confidence: {(faceDetection.score * 100).toFixed(0)}%
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {faceDetection.warning && (
                        <p className="text-sm font-medium text-[var(--color-warning-700)]">
                          ⚠ {faceDetection.warning}
                        </p>
                      )}
                      {faceDetection.guide && (
                        <p className="text-xs text-[var(--color-warning-600)]">
                          {faceDetection.guide}
                        </p>
                      )}
                      {faceDetection.detected && (
                        <p className="text-xs text-[var(--color-warning-600)]">
                          Confidence: {(faceDetection.score * 100).toFixed(0)}%
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {videoReady && !capturing && !faceDetection && modelsLoaded && (
                <div className="p-3 bg-[var(--color-neutral-50)] border border-[var(--color-neutral-border)] rounded-lg">
                  <p className="text-sm text-[var(--color-subtext-color)]">
                    Detecting face... Please position yourself in front of the camera.
                  </p>
                </div>
              )}
              {!videoReady && (
                <div className="text-center space-y-2">
                  <p className="text-xs text-[var(--color-subtext-color)]">
                    Please wait for the camera to initialize before capturing.
                  </p>
                  {error && (
                    <p className="text-xs text-[var(--color-error-600)]">{error}</p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={stopCapture}
                  >
                    Cancel
                  </Button>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={captureBiometric}
                  disabled={disabled || capturing || !videoReady || !modelsLoaded || !faceDetection?.detected}
                  isLoading={capturing}
                >
                  {capturing ? 'Processing...' : 'Capture Biometric'}
                </Button>
                <Button
                  variant="outline"
                  onClick={stopCapture}
                  disabled={disabled || capturing}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BiometricCapture;

